import { alpha, lighten } from '@mui/material/styles';
import { colors, typography } from '../../styles/theme';
import { LEADS_SKELETON_MUTED, LEADS_SKELETON_PRIMARY } from '../../Leads/components/leadsUi.styles';
import { DOCK_SCROLLBAR_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';

/** Paper del panel: mismo chrome que Leads (fondo surface, borde, radio 16). */
export const metaAdsPanelPaperSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: '100%',
  minHeight: '100%',
  bgcolor: colors.backgroundSurface,
  backgroundImage: 'none',
  color: colors.textPrimary,
  boxShadow: '0 18px 45px rgba(15,23,42,0.55)',
  border: `1px solid ${colors.border}`,
  borderRadius: { xs: 0, sm: '16px' },
};

export const metaAdsBodySx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  // Scroll estándar del Dock (fina y gris, no la blanca del navegador), como DockPanel.
  scrollbarGutter: 'stable',
  ...DOCK_SCROLLBAR_SX,
  px: 2.5,
  py: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

export const metaAdsCardSx = {
  borderRadius: '12px',
  border: '1px solid rgba(255,255,255,0.08)',
  bgcolor: colors.backgroundDark,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

export const metaAdsCardTitleSx = {
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '15px',
  lineHeight: '20px',
  color: colors.textPanelTitle,
};

export const metaAdsMutedTextSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  lineHeight: '18px',
  color: 'rgba(255,255,255,0.55)',
};

export const metaAdsBodyTextSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '14px',
  lineHeight: '20px',
  color: colors.textPrimary,
};

export const metaAdsProgressSx = {
  height: 6,
  borderRadius: '999px',
  bgcolor: 'rgba(255,255,255,0.08)',
  '& .MuiLinearProgress-bar': { bgcolor: colors.primary, borderRadius: '999px' },
};

export const metaAdsStepRowSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.25,
};

export const metaAdsStepNumberSx = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: '1px solid rgba(255,255,255,0.28)',
  color: 'rgba(255,255,255,0.7)',
  fontSize: '12px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  mt: '1px',
};

export const metaAdsStepTitleSx = {
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.semibold,
  fontSize: '14px',
  lineHeight: '22px',
  color: colors.textPrimary,
};

const STATUS_TONES = {
  done: { color: colors.primaryLight, bg: alpha(colors.primary, 0.18) },
  partial: { color: colors.primaryLight, bg: alpha(colors.primary, 0.1) },
  warning: { color: colors.warning, bg: alpha(colors.warning, 0.14) },
  danger: { color: colors.error, bg: alpha(colors.error, 0.14) },
  pending: { color: 'rgba(255,255,255,0.6)', bg: 'rgba(255,255,255,0.06)' },
};

/** Píldora de estado de un paso del checklist. */
/** Punto de entrega (~8px) junto al nombre; colores de `metaDeliveryDot`. */
const DELIVERY_DOT_COLORS = {
  green: lighten(colors.success, 0.25),
  amber: colors.warning,
  red: colors.error,
  gray: 'rgba(255,255,255,0.35)',
};
export const getMetaAdsDeliveryDotSx = (dot) => ({
  display: 'inline-block',
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  bgcolor: DELIVERY_DOT_COLORS[dot] || DELIVERY_DOT_COLORS.gray,
});

export const getMetaAdsStatusPillSx = (tone) => {
  const t = STATUS_TONES[tone] || STATUS_TONES.pending;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: 22,
    px: 1,
    borderRadius: '999px',
    bgcolor: t.bg,
    color: t.color,
    fontFamily: typography.fontFamily.primary,
    fontSize: '12px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };
};

export const metaAdsStepIconSx = (tone) => ({
  fontSize: 22,
  flexShrink: 0,
  mt: '1px',
  color: tone === 'warning' ? colors.warning : colors.primary,
});

/**
 * Disabled legible de los botones `contained` del panel: con el tema claro de MUI
 * quedaba texto oscuro sobre fondo oscuro. Gris, sin verde. Mismos tokens que el tema
 * (`disabledOnDark*`, docs/frontend/botones-y-estados.md).
 */
export const metaAdsContainedDisabledSx = {
  '&.Mui-disabled': {
    bgcolor: colors.disabledOnDarkBg,
    color: colors.disabledOnDarkText,
  },
};

export const metaAdsPrimaryButtonSx = {
  alignSelf: 'flex-start',
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: 'none',
  ...metaAdsContainedDisabledSx,
};

/** Botón de solo ícono del panel (p. ej. «Actualizar lista»): mismo tamaño que la flecha de volver. */
/**
 * Ícono de acción en el header del panel, a la izquierda de la ✕ (mismo trato que
 * «Mostrar en el mapa» / «Encuadrar en el mapa» del header de Registros).
 */
export const metaAdsPanelHeaderIconSx = {
  color: '#E8F4F4',
  '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
  '&.Mui-disabled': { color: 'rgba(232,244,244,0.35)' },
};

export const metaAdsIconButtonSx = {
  p: 0.5,
  color: colors.primaryLight,
  '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
};

/** CTA principal de la puesta en marcha: ocupa el ancho de la tarjeta. */
export const metaAdsHeroButtonSx = {
  ...metaAdsPrimaryButtonSx,
  alignSelf: 'stretch',
};

/**
 * Sin Meta: la tarjeta de conectar ocupa el alto libre y su contenido (ícono, título,
 * texto, botón) va centrado como un solo bloque. El aviso del editor sigue alineado a la izquierda.
 */
export const metaAdsConnectHeroSx = {
  ...metaAdsCardSx,
  flex: 1,
  minHeight: 0,
  p: 3,
  gap: 1.5,
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  borderColor: alpha(colors.primary, 0.55),
  '& > *': { width: '100%', maxWidth: 360 },
  '& [data-testid="meta-ads-editor-notice"]': { textAlign: 'left' },
  '& .MuiButton-contained': {
    mt: 0.5,
    minHeight: 48,
    fontSize: '16px',
  },
};

/** Ícono grande de la tarjeta de conectar (mismo teal del panel). */
export const metaAdsConnectHeroIconSx = {
  // `&&` gana al `& > *` (ancho completo) de la tarjeta.
  '&&': { width: 64 },
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: alpha(colors.primary, 0.18),
  color: colors.primaryLight,
  mb: 0.5,
};

/** Título de la tarjeta de conectar. */
export const metaAdsConnectHeroTitleSx = {
  ...metaAdsCardTitleSx,
  fontSize: '20px',
  lineHeight: '26px',
  color: colors.textPanelDescription,
};

/**
 * Mientras conecta el botón queda disabled, pero el tema de MUI es claro:
 * el disabled pinta fondo y texto negros y se pierde sobre la tarjeta.
 * Se mantiene el teal con el texto blanco.
 */
export const metaAdsPrimaryButtonLoadingSx = {
  '&&.Mui-disabled': {
    bgcolor: colors.primary,
    color: colors.backgroundLight,
  },
};

/** Lo que habilita conectar: acompaña a la tarjeta, sin tarjeta propia. Flechas a los costados. */
export const metaAdsWhyConnectSx = {
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1,
  pt: 0.5,
};

/** Título chico y tenue de «Para qué conectar». */
export const metaAdsWhyConnectTitleSx = {
  ...metaAdsMutedTextSx,
  fontSize: '12px',
  fontWeight: typography.fontWeight.semibold,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

/** Ícono de cada capacidad: mismo teal del resto del panel. */
export const metaAdsCapabilityIconSx = {
  width: 40,
  height: 40,
  borderRadius: '10px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: alpha(colors.primary, 0.18),
  color: colors.primaryLight,
};

export const metaAdsPlainListSx = {
  listStyle: 'none',
  m: 0,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
};

/** Skeleton: misma paleta que Registros / Listas / Leads. */
export const metaAdsSkeletonSx = { bgcolor: LEADS_SKELETON_PRIMARY, borderRadius: '6px' };
// Tono apagado para líneas secundarias y chips (como `historySkeletonMutedSx` / `LoadingRecordCards`).
export const metaAdsSkeletonMutedSx = { bgcolor: LEADS_SKELETON_MUTED, borderRadius: '6px' };

/** Acción secundaria (desconectar): gris del panel, blanco al pasar. */
export const metaAdsQuietButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  alignSelf: 'flex-start',
  px: 1,
  minWidth: 0,
  color: colors.textSecondary,
  '&:hover': { color: colors.textPanelDescription, bgcolor: 'rgba(255,255,255,0.06)' },
  // Tema claro de MUI: el disabled queda negro sobre el panel. Gris más tenue, legible.
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
};

/** «Desconectar Meta» al pie de la puesta en marcha: baja al final del panel. */
export const metaAdsDisconnectRowSx = {
  display: 'flex',
  justifyContent: 'center',
  mt: 'auto',
  pt: 0.5,
};

/** Pie de una vista del panel (p. ej. «1 elegida» + «Seguir estas cuentas»): baja al final. */
export const metaAdsStickyFooterSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  mt: 'auto',
  pt: 0.5,
};

/** «Ver» (abre Meta) en el vértice derecho de una fila, a la altura del título. */
export const metaAdsRowOpenLinkSx = {
  ml: 'auto',
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '20px',
  color: colors.primaryLight,
};

/** Paso que toca ahora en la puesta en marcha: mismo borde teal que la tarjeta de conectar. */
export const metaAdsCurrentStepCardSx = {
  ...metaAdsCardSx,
  borderColor: alpha(colors.primary, 0.55),
};

/** «Análisis» / «Informe» del encabezado de Campañas: solo ícono, color primario (Tooltip con el nombre). */
export const metaAdsHeaderIconActionSx = {
  p: 0.5,
  flexShrink: 0,
  color: colors.primaryLight,
  '&:hover': { color: colors.primaryLight, bgcolor: alpha(colors.primary, 0.12) },
};


/** «Ver informe» de una fila de Informe: texto teal, arriba a la derecha, sin borde. */
export const metaReportUnitActionSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  lineHeight: '20px',
  flexShrink: 0,
  minWidth: 0,
  minHeight: 20,
  px: 1,
  py: 0,
  mr: -1,
  whiteSpace: 'nowrap',
  color: colors.primaryLight,
  '&:hover': { bgcolor: alpha(colors.primary, 0.12) },
};

/*
 * Jerarquía campaña → anuncios (mockup aprobado `campaigns-hierarchy`): etiqueta de
 * nivel, subtítulo, lista con sangría y línea guía, bloque de anuncio más suave.
 */
const LEVEL_TAGS = {
  campaign: { bgcolor: alpha(colors.primary, 0.16), color: colors.primaryLight },
  ad: { bgcolor: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)' },
};
/** Etiqueta chica «Campaña» (verde Terrio) / «Anuncio» (gris). */
export const getMetaAdsLevelTagSx = (level) => ({
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'flex-start',
  height: 18,
  px: 0.75,
  borderRadius: '4px',
  fontFamily: typography.fontFamily.primary,
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  lineHeight: 1,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  ...(LEVEL_TAGS[level] || LEVEL_TAGS.ad),
});

/** «Anuncios de esta campaña (N)». */
export const metaAdsSectionTitleSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.7)',
  mt: 0.5,
};

/** Lista de anuncios: sangría + línea guía verde tenue a la izquierda. */
export const metaAdsAdListSx = {
  listStyle: 'none',
  m: 0,
  ml: 0.75,
  p: 0,
  pl: 1.5,
  borderLeft: `2px solid ${alpha(colors.primaryLight, 0.35)}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

/** Bloque de un anuncio (y el compacto de «1 anuncio»): fondo más suave que la tarjeta. */
export const metaAdsAdBlockSx = {
  bgcolor: 'rgba(255,255,255,0.035)',
  borderRadius: '8px',
  p: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

/**
 * Encabezado del bloque compacto (1 anuncio): etiqueta | punto + nombre | píldora.
 * La primera línea mide 22 px (alto de la píldora; el nombre usa line-height 22 y la
 * etiqueta de 18 px baja 2 px), así quedan centradas aunque el nombre envuelva.
 */
export const metaAdsCompactHeadSx = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  alignItems: 'start',
  columnGap: 1,
};

/** Etiqueta de nivel dentro de una fila de 22 px (centrada con la primera línea). */
export const metaAdsTagInLineSx = { mt: '2px' };

/** Caja del alto de una línea (22 px) para centrar un punto / ícono con el texto. */
export const metaAdsLineBoxSx = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 22,
  flexShrink: 0,
};

/** Métricas propias del anuncio (lo primero que se lee). */
export const metaAdsAdMetricsTextSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: colors.textPrimary,
};

/** Nombre de Meta del anuncio: más chico y gris. */
export const metaAdsAdNameTextSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '12.5px',
  lineHeight: '17px',
  color: 'rgba(255,255,255,0.62)',
  overflowWrap: 'anywhere',
};

/**
 * Vista Campañas: columna que ocupa el cuerpo del panel (que en esta vista no
 * scrollea, ver `metaAdsBodyFixedSx`). Header fijo arriba + lista que scrollea.
 */
export const metaCampaignsLayoutSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  height: '100%',
};

/*
 * Header y lista usan el mismo padding horizontal y el mismo `scrollbarGutter`
 * (el header con overflow hidden también reserva el canal de 6 px), así el borde
 * derecho de los filtros y de las tarjetas coincide y nada salta al aparecer la barra.
 */
const SAME_GUTTER = { scrollbarGutter: 'stable', ...DOCK_SCROLLBAR_SX };

/** Header fijo de Campañas (fuera del scroll), con separador sutil siempre. */
export const metaCampaignsHeaderSx = {
  flexShrink: 0,
  overflow: 'hidden',
  ...SAME_GUTTER,
  px: 2.5,
  pt: 2,
  pb: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

// Análisis general e Informe general (t1056u): primera fila del encabezado con la frescura a la
// izquierda y los controles a la derecha, centrados. Con `flex-wrap` los controles bajan a una
// segunda línea (alineada a la derecha) solo si no entran; la frescura no se achica antes de bajar.
// Alto mínimo = el del select (30 px) con o sin frescura: no salta al llegar el dato.
export const META_SECTION_TOP_ROW_PX = 30;
export const metaSectionTopRowSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: 1,
  rowGap: 1,
  minHeight: META_SECTION_TOP_ROW_PX,
};
export const metaSectionFreshnessSlotSx = { flex: '1 0 auto', minWidth: 0, maxWidth: '100%' };
export const metaSectionControlsSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 1,
  ml: 'auto',
  flex: '0 0 auto',
};
// Con selector de campaña: base 280 px (nombre legible), crece hasta ocupar la fila si baja.
export const metaSectionControlsWithScopeSx = { ...metaSectionControlsSx, flex: '1 1 280px', minWidth: 0, maxWidth: '100%' };

/** Lista de Campañas: la única zona que scrollea (scroll del Dock + fade). */
// Padding de arriba de la lista (px): la cabecera sticky de la tarjeta abierta lo compensa con su
// `top` negativo (Chrome / Safari pegan el sticky en el borde del contenido, no del scroller).
export const META_CAMPAIGNS_LIST_PAD_TOP = 12;
export const metaCampaignsListSx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  ...SAME_GUTTER,
  px: 2.5,
  pt: `${META_CAMPAIGNS_LIST_PAD_TOP}px`,
  pb: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

/** Contenedor de la pestaña Campañas cuando queda montada junto a las otras: ocupa todo el cuerpo. */
export const metaAdsTabPaneSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

/** Cuerpo del panel cuando la vista maneja su propio scroll (Campañas): sin scroll ni padding. */
export const metaAdsBodyFixedSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

// Toggle «Activas · Todas»: mismo chrome que el resto del panel (bordes suaves, teal al elegir).
export const metaViewToggleSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: 1.4,
    px: 1.25,
    py: 0.25,
    color: 'rgba(255,255,255,0.7)',
    borderColor: 'rgba(255,255,255,0.16)',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
    '&.Mui-selected': {
      color: colors.primaryLight,
      bgcolor: alpha(colors.primary, 0.18),
      '&:hover': { bgcolor: alpha(colors.primary, 0.24) },
    },
  },
};
// Desplegable «Activas (N) / Todas (M)» al lado de un buscador de 44 px (`listSearchFieldSx`, t999u;
// antes un toggle): mismo alto, radio, fondo y borde que el input; foco en el primario de Terrio y
// deshabilitado en gris legible. Solo en «Vincular campaña».
export const metaLinkFilterSelectSx = {
  height: 44,
  minWidth: 128,
  borderRadius: '8px',
  bgcolor: '#141414',
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(255,255,255,0.92)',
  '& .MuiSelect-select': { py: '12px', lineHeight: '20px', minHeight: 0, pl: 1.5, pr: '32px !important', boxSizing: 'border-box' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--border-default, #4A4A4A)', borderWidth: '1px' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primaryLight, borderWidth: '2px' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.04)' },
  '&.Mui-disabled .MuiSelect-select': { color: colors.disabledOnDarkText, WebkitTextFillColor: colors.disabledOnDarkText },
  '&.Mui-disabled .MuiSvgIcon-root': { color: colors.disabledOnDarkText },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
};
// Selector de período (ajuste 10): chico, mismo borde que el toggle.
// «Gasto total ARS X ⓘ» en la fila del período (t989u): etiqueta gris, monto destacado, sin cortar.
export const metaCampaignsSpendSx = { display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: 28, maxWidth: '100%' };
export const metaCampaignsSpendTextSx = { fontSize: '12px', lineHeight: '16px', textAlign: 'right' };
export const metaCampaignsSpendLabelSx = { color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' };
export const metaCampaignsSpendAmountSx = { color: 'rgba(255,255,255,0.92)', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' };

export const metaPeriodSelectSx = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.85)',
  '& .MuiSelect-select': { py: 0.5, pl: 1.25, pr: '28px !important' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.16)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
};

// Cabecera de la tarjeta de campaña (t1052u): sin nombre ni punto (están en el buscador). Una sola
// línea de alto fijo (28 px): objetivo (trunca, completo en el title) y chips de estado (no se
// achican) a la izquierda; el ícono de informe, «+» y ⋮ a la derecha.
export const META_CAMPAIGN_HEAD_LINE_PX = 28;
export const metaCampaignHeadContainerSx = { containerType: 'inline-size' };
export const metaCampaignHeadLineSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  height: META_CAMPAIGN_HEAD_LINE_PX,
  minWidth: 0,
};
export const metaCampaignHeadChipsSx = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: 0.5,
  flexShrink: 0,
  '& > *': { flexShrink: 0, whiteSpace: 'nowrap' },
};
// Chip de unidad vinculada («Unidad: …» / «Lote: …»): tinte y borde del acento turquesa (como el
// toggle elegido, `metaViewToggleSx`), texto claro (AA sobre el fondo oscuro), ícono de propiedad y
// ✕ visible. Con ficha resoluble, clickeable (hover / foco más marcados).
export const metaLinkedRecordChipIconSx = { fontSize: '15px !important', color: `${colors.primaryLight} !important`, ml: '6px !important' };
export const metaLinkedRecordChipSx = (openable = false) => ({
  maxWidth: '100%',
  height: 26,
  bgcolor: alpha(colors.primary, 0.18),
  border: `1px solid ${alpha(colors.primaryLight, 0.55)}`,
  color: '#E8F4F4',
  fontSize: '12px',
  fontWeight: 600,
  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', px: '8px' },
  '& .MuiChip-deleteIcon': {
    fontSize: 16, color: 'rgba(232,244,244,0.75)', mr: '5px',
    '&:hover': { color: '#FFFFFF' },
  },
  ...(openable ? {
    cursor: 'pointer',
    '&:hover': { bgcolor: alpha(colors.primary, 0.28), borderColor: colors.primaryLight },
    '&.Mui-focusVisible': { bgcolor: alpha(colors.primary, 0.28), outline: `2px solid ${colors.primaryLight}`, outlineOffset: 1 },
  } : {}),
});
// «Unidades vinculadas» de la campaña (t994u): registros en filas como la lista «Personas» del modal
// de Compartir (`ShareUserLotListDialog`): avatar circular verde Terrio con el ícono de unidad / lote,
// nombre (abre la ficha si se puede), etiqueta gris a la derecha y ✕ (solo quien puede desvincular).
export const metaLinkedUnitsTitleSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
  lineHeight: '16px', color: 'rgba(255,255,255,0.6)', pt: 1, pb: 0.25,
};
export const metaLinkedUnitsListSx = { listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0 };
// t996u: más compacta (nombre 14 px / 500 como el texto del anuncio, avatar 28 px con ícono 16, ✕ de
// ~18 px, 6 px de padding vertical); borde izquierdo alineado con «Anuncios de esta campaña».
export const META_LINKED_UNIT_AVATAR = 28;
export const META_LINKED_UNIT_ICON = 16;
export const metaLinkedUnitRowSx = { display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 0 };
export const metaLinkedUnitAvatarSx = {
  width: META_LINKED_UNIT_AVATAR, height: META_LINKED_UNIT_AVATAR, borderRadius: '50%', bgcolor: colors.primary, color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};
const metaLinkedUnitNameBaseSx = {
  flex: 1, minWidth: 0, color: '#F9FAFB', fontSize: '14px', lineHeight: '20px', fontWeight: 500, fontFamily: typography.fontFamily.primary,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left',
};
export const metaLinkedUnitNameSx = metaLinkedUnitNameBaseSx;
export const metaLinkedUnitNameButtonSx = {
  ...metaLinkedUnitNameBaseSx,
  // Sin `font: inherit` (pisaba el 14 px / 500 y el botón heredaba el tamaño del contenedor).
  display: 'block', p: 0, m: 0, border: 0, background: 'none', cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
  '&:focus-visible': { outline: `2px solid ${colors.primaryLight}`, outlineOffset: 2, borderRadius: '2px' },
  '&[aria-busy="true"]': { cursor: 'progress', opacity: 0.7 },
};
export const metaLinkedUnitTagSx = { flexShrink: 0, color: '#ABABAB', fontSize: '12px', lineHeight: '16px', px: 1, whiteSpace: 'nowrap' };
export const metaLinkedUnitRemoveSx = {
  color: '#ABABAB', flexShrink: 0, p: '1px', width: 18, height: 18,
  '&:hover': { color: '#F9FAFB', bgcolor: 'rgba(255,255,255,0.06)' },
};
// Cabecera de la tarjeta de campaña abierta, sticky dentro de su tarjeta (el bloque que la contiene
// es la tarjeta: no se monta sobre la siguiente). Pegada queda a META_STICKY_HEAD_AIR px del borde
// de arriba de la lista (`top` = −padding de la lista + aire).
// - Ocupa el padding y el borde de la tarjeta (mx/mt −17 px, borde 1 px, px/pt 16 px) con fondo
//   opaco y su propio borde arriba / a los costados, opaco y del mismo tono que el de la tarjeta
//   (rgba(255,255,255,.08) sobre #242424 = #343434, medido en Chrome): sin pegar, se dibuja exactamente encima del
//   borde de la tarjeta (se ve igual); pegada, la cabecera conserva su borde y radios de arriba.
// - `::before`: franja de META_STICKY_HEAD_AIR px arriba, con el fondo de la lista: pegada tapa el
//   contenido que pasa por ese hueco (se ve el fondo de la lista); sin pegar cae sobre el gap entre
//   tarjetas (mismo fondo, invisible). Baja también 12 px sobre las esquinas de arriba y pinta el
//   fondo de la lista solo fuera de la curva: pegada, los bordes laterales rectos de la tarjeta (que
//   sigue debajo) no asoman junto al radio. Sin pegar esa zona ya está fuera de la tarjeta.
// - Línea inferior = `border-bottom` del mismo elemento (antes `box-shadow`: cruzaba los bordes
//   laterales y dejaba «puntitas» más claras en las esquinas). pb 4 px + borde 1 px compensados con
//   mb −5 px: mismo alto y posición pegada o no (no salta).
// - `::after` (solo pegada, `data-stuck`): el fade de scroll de Terrio debajo de la cabecera (mismo
//   tamaño que `useScrollEdgeFade`, 24 px; acá es un gradiente del fondo de la tarjeta porque el
//   contenido pasa por debajo de un elemento, no del borde del scroller). Dentro de los bordes
//   laterales, sin tapar la cabecera; sin scroll no aparece.
export const META_STICKY_HEAD_AIR = 8;
// Mismo tamaño que el fade de `useScrollEdgeFade` (DEFAULT_FADE_SIZE).
const META_STICKY_HEAD_FADE = 24;
// Radio de arriba de la cabecera (= el de la tarjeta).
const META_STICKY_HEAD_RADIUS = 12;
const META_CARD_BORDER_OPAQUE = '#343434';
export const metaCampaignStickyHeadSx = {
  position: 'sticky',
  top: META_STICKY_HEAD_AIR - META_CAMPAIGNS_LIST_PAD_TOP,
  zIndex: 3,
  bgcolor: colors.backgroundDark,
  border: `1px solid ${META_CARD_BORDER_OPAQUE}`,
  borderTopLeftRadius: `${META_STICKY_HEAD_RADIUS}px`,
  borderTopRightRadius: `${META_STICKY_HEAD_RADIUS}px`,
  mx: '-17px',
  px: 2,
  mt: '-17px',
  pt: 2,
  pb: 0.5,
  mb: '-5px',
  // rgba(255,255,255,.06) sobre #242424 (la línea de antes), opaco.
  borderBottom: '1px solid #313131',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -1,
    right: -1,
    top: -(META_STICKY_HEAD_AIR + 1),
    // Hueco (8 px) + las dos esquinas de arriba (radio 12 px): fuera de la curva, fondo de la lista;
    // dentro, transparente (se ve la cabecera). Pegada, la tarjeta sigue debajo con sus bordes
    // laterales rectos: sin esto asomaban por fuera de la curva («puntitas» de los vértices).
    height: META_STICKY_HEAD_AIR + META_STICKY_HEAD_RADIUS,
    background: [
      `radial-gradient(circle at 100% 100%, transparent ${META_STICKY_HEAD_RADIUS}px, ${colors.backgroundSurface} ${META_STICKY_HEAD_RADIUS + 0.5}px) left bottom / ${META_STICKY_HEAD_RADIUS}px ${META_STICKY_HEAD_RADIUS}px no-repeat`,
      `radial-gradient(circle at 0 100%, transparent ${META_STICKY_HEAD_RADIUS}px, ${colors.backgroundSurface} ${META_STICKY_HEAD_RADIUS + 0.5}px) right bottom / ${META_STICKY_HEAD_RADIUS}px ${META_STICKY_HEAD_RADIUS}px no-repeat`,
      `linear-gradient(${colors.backgroundSurface}, ${colors.backgroundSurface}) left top / 100% ${META_STICKY_HEAD_AIR}px no-repeat`,
    ].join(', '),
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    // 100 % = borde del padding box: +1 px para quedar debajo de la línea inferior, sin taparla.
    top: 'calc(100% + 1px)',
    height: META_STICKY_HEAD_FADE,
    background: `linear-gradient(to bottom, ${colors.backgroundDark}, ${alpha(colors.backgroundDark, 0)})`,
    pointerEvents: 'none',
    opacity: 0,
  },
  '&[data-stuck="true"]::after': { opacity: 1 },
};
// Barra Resultados · Análisis · Presupuesto · Informe del detalle de campaña: 12 px más arriba
// (16 px desde la línea de la cabecera abierta; va en el contenido, la cabecera sticky no cambia)
// y 4 px más abajo (12 px hasta el primer título de la sección, p. ej. «RESULTADOS»).
export const metaCampaignSectionsToggleSx = { mt: 1.5, mb: 0.5 };
